'use client';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import React, { SVGProps, useMemo } from 'react';

import {
  AbgTypography,
  CheckIcon,
  CloseIcon,
  formatMoney,
} from '@abg/kit-base';
import { AbgSxProps } from '@abg/typings';

import { useAnalyticsGeneralEngagement } from '../../../../shared/analytics/hooks/use-analytics-general-engagement';
import { useTranslationContext } from '../../../../shared/context';
import { AncillariesI18nKeys } from '../../../../shared/types';
import { sanitizeHtml } from '../../../../shared/utils';
import { mapTableRows } from '../utils/map-table-rows';

import { ProtectionsComparisonDesktopProps } from './types';

export const ProtectionsComparisonDesktop = ({
  styleParts = {},
  bundles,
  onClose,
  onBundleSelected,
}: ProtectionsComparisonDesktopProps) => {
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();

  const { emitEvent: emitGeneralEngagementEvent } =
    useAnalyticsGeneralEngagement();

  const rows = useMemo(() => mapTableRows(bundles), [bundles]);

  const handleSelection = (bundleCode: string) => {
    onBundleSelected(bundleCode);
    onClose();
  };

  return (
    <Dialog
      open
      sx={styleParts.dialog as AbgSxProps}
      data-testid="protections-comparison-compare-bundles-card-container"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={styleParts.dialogHeader as AbgSxProps}
      >
        <DialogTitle
          sx={styleParts.dialogTitle as AbgSxProps}
          data-testid="protections-comparison-compare-bundles-card-title"
        >
          <AbgTypography variant="bodyLargeRegular">
            {t(i18nKeys.compareBundlesLabel)}
          </AbgTypography>
        </DialogTitle>
        <DialogActions sx={{ padding: 0 }}>
          <CloseIcon
            data-testid="protections-comparison-close-btn"
            sx={{ ...styleParts.closeDialogIconDimensions }}
            strokeWidth={
              (styleParts.closeDialogIcon as SVGProps<SVGPathElement>)
                ?.strokeWidth
            }
            onClick={(event) => {
              emitGeneralEngagementEvent(
                event.currentTarget,
                'ProtectionsComparisonDesktop',
              );
              onClose();
            }}
          />
        </DialogActions>
      </Stack>
      <DialogContent sx={styleParts.dialogContent as AbgSxProps}>
        <Stack>
          <Stack direction="row" alignItems="flex-end">
            <Box sx={styleParts.headerCellFirst as AbgSxProps} />
            {bundles.map((bundle) => (
              <Stack
                key={bundle?.title}
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                data-testid="protections-comparison-bundle-header"
                sx={
                  {
                    ...styleParts.headerCell,
                    ...(bundle?.recommended
                      ? styleParts.recommendedColumnTop
                      : {}),
                  } as AbgSxProps
                }
              >
                <Stack
                  sx={{
                    ...(bundle?.recommended
                      ? styleParts.recommendedWrapper
                      : {}),
                  }}
                >
                  {bundle.recommended && (
                    <AbgTypography
                      variant="tagRegular"
                      align="center"
                      sx={styleParts.recommended as AbgSxProps}
                      data-testid="protections-comparison-bundle-recommended"
                    >
                      {t(i18nKeys.recommendedLabel)}
                    </AbgTypography>
                  )}
                </Stack>
                <Stack>
                  <AbgTypography
                    variant="bodyRegularBold"
                    sx={styleParts.headerCellTitle as AbgSxProps}
                    data-testid="protections-comparison-bundle-name"
                  >
                    {bundle.title}
                  </AbgTypography>
                  <AbgTypography
                    component="p"
                    variant="bodySmallRegular"
                    sx={styleParts.headerCellPrice as AbgSxProps}
                    data-testid="protections-comparison-bundle-price"
                  >
                    {formatMoney(Number(bundle.price), {
                      currency: bundle?.currencyCode || 'USD',
                    })}{' '}
                    / {t(i18nKeys.dayLabel)}
                  </AbgTypography>
                </Stack>
              </Stack>
            ))}
          </Stack>

          {rows.map((row, rowIndex) => (
            <Stack
              key={rowIndex}
              direction="row"
              alignItems="stretch"
              data-testid="protections-comparison-single-protection-row"
            >
              <Stack sx={styleParts.rowCellFirst as AbgSxProps}>
                <AbgTypography
                  sx={styleParts.rowCellFirstTitle as AbgSxProps}
                  variant="bodyRegularMedium"
                  data-testid="protections-comparison-single-protection-name"
                >
                  {row[0]?.title}
                </AbgTypography>
                <AbgTypography
                  variant="bodySmallRegular"
                  data-testid="protections-comparison-single-protection-description"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(row[0]?.description || ''),
                  }}
                />
              </Stack>
              {row.map((item, index) => (
                <Box
                  key={`${item?.title}-${index}`}
                  sx={
                    {
                      ...styleParts.rowCell,
                      ...(item?.recommended
                        ? styleParts.recommendedColumnBackground
                        : {}),
                    } as AbgSxProps
                  }
                >
                  {item?.included ? (
                    <CheckIcon
                      data-testid="protections-comparison-item-included-icon"
                      sx={styleParts.checkItemIconDimensions as AbgSxProps}
                      stroke={
                        (styleParts.checkItemIcon as SVGProps<SVGPathElement>)
                          ?.stroke
                      }
                      strokeWidth={
                        (styleParts.checkItemIcon as SVGProps<SVGPathElement>)
                          ?.strokeWidth
                      }
                    />
                  ) : (
                    <CloseIcon
                      data-testid="protections-comparison-item-excluded-icon"
                      sx={styleParts.closeItemIconDimensions as AbgSxProps}
                      stroke={
                        (styleParts.closeItemIcon as SVGProps<SVGPathElement>)
                          ?.stroke
                      }
                      strokeWidth={
                        (styleParts.closeItemIcon as SVGProps<SVGPathElement>)
                          ?.strokeWidth
                      }
                    />
                  )}
                </Box>
              ))}
            </Stack>
          ))}

          <Stack direction="row" alignItems="center">
            <Box sx={styleParts.actionRowCellFirst as AbgSxProps} />
            {bundles.map((bundle) => (
              <Stack
                key={`${bundle?.title}-btn`}
                justifyContent="center"
                alignItems="center"
                sx={
                  {
                    ...styleParts.actionRowCell,
                    ...(bundle.recommended
                      ? styleParts.recommendedColumnBottom
                      : {}),
                  } as AbgSxProps
                }
              >
                <Button
                  variant="contained"
                  sx={styleParts.button as AbgSxProps}
                  onClick={() => handleSelection(bundle?.code)}
                  data-testid="protections-comparison-add-to-trip-btn"
                >
                  <AbgTypography
                    variant="actionRegularRegular"
                    sx={styleParts.buttonText as AbgSxProps}
                  >
                    {t(i18nKeys.addToTripLabel)}
                  </AbgTypography>
                </Button>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
